import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  Box,
  Button,
  DataTableFilterButton,
  DataTableToolbar,
  DataTableToolbarActions,
  Icon,
  Separator,
  Text,
  useIsMobile,
  type BulkAction,
} from "@salestrak/ui";
import { ViewSearch } from "#app/components/filters/view-search";
import { PageHeader } from "#app/components/page-header";
import {
  TEAMS_SETTINGS_DEFAULT_HIDDEN_COLUMN_IDS,
  TeamsTable,
  buildTeamsFilterDefs,
  useTeamsColumns,
} from "#app/components/teams/teams-table";
import { CreateTeamDialog } from "#app/components/teams/create-team-dialog";
import { useListQueryState } from "#app/lib/hooks/use-list-query-state";
import { TEAM_LIST } from "#app/lib/filters/list-configs";
import type { TeamRow } from "#app/lib/data";
import { useTeamMutations } from "#app/lib/hooks/use-team-mutations";
import { useUserPool } from "#app/components/entity-selectors/hooks/use-user-pool";
import { renderTableEntityFilter } from "#app/components/entity-selectors/table-entity-filter";
import { settleAll } from "#app/lib/utils/settle-all";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { usePermission } from "#app/lib/hooks/use-permission";
import { useTableFilters } from "#app/lib/hooks/use-table-filters";
import { useTableViewSettings } from "#app/lib/hooks/use-table-view-settings";
import { withPermission } from "#app/components/permissions/require-permission";

function SettingsTeamsPage() {
  const { push } = useAppNavigate();
  const isMobile = useIsMobile();
  const canCreateTeam = usePermission("manage.teams");

  // The windowed pool: the funnel's member lists page and search the server.
  const { pool: userPool } = useUserPool();
  const filterDefs = useMemo(
    () => buildTeamsFilterDefs(userPool),
    [userPool],
  );
  const teamFilters = useTableFilters(filterDefs);

  const { searchValue, setSearchValue, sorting, setSorting, where, orderBy } =
    useListQueryState(TEAM_LIST, { filterWhere: teamFilters.where });

  const { deleteTeam } = useTeamMutations();

  const [teamTotal, setTeamTotal] = useState<number | undefined>(undefined);
  const isTrulyEmpty =
    teamTotal === 0 && !searchValue && teamFilters.activeFilterCount === 0;

  // --- Bulk actions config (settings variant only) ---
  const bulkActions = useMemo<BulkAction<TeamRow>[]>(
    () => [
      {
        id: "delete",
        type: "confirm",
        label: "Delete",
        dialogLabel: "Delete...",
        icon: "trash",
        destructive: true,
        // The route's manage.teams gate is workspace-wide; the server's finer
        // rule is per team. The table seats a checkbox only on teams the
        // viewer can manage (`canSelectRow`), so every selection is deletable.
        confirmTitle: (rows) =>
          `Delete ${rows.length} team${rows.length !== 1 ? "s" : ""}?`,
        confirmDescription: (rows) =>
          `This will permanently delete ${rows.length} team${rows.length !== 1 ? "s" : ""}. Anything assigned to these teams is unassigned. This cannot be undone.`,
        confirmLabel: "Delete Teams",
        onAction: async (selected) => {
          await settleAll(selected, (t) => deleteTeam(t.teamId));
        },
      },
    ],
    [deleteTeam],
  );

  // Dialog state
  const [deleteTarget, setDeleteTarget] = useState<{
    teamId: string;
    teamName: string;
  } | null>(null);

  const handleNavigateToTeam = useCallback(
    (teamId: string) => {
      push(`/settings/teams/${teamId}`);
    },
    [push],
  );

  // The Members cell leaves settings: membership lives on the team page's
  // own roster tab, which is where every add/remove affordance is.
  const handleViewTeamMembers = useCallback(
    (teamId: string) => {
      push(`/team/${teamId}/members`);
    },
    [push],
  );

  const handleDeleteTeam = useCallback((teamId: string, teamName: string) => {
    setDeleteTarget({ teamId, teamName });
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    await deleteTeam(deleteTarget.teamId);
    setDeleteTarget(null);
  }, [deleteTarget, deleteTeam]);

  // Creation is the shared CreateTeamDialog everywhere — the old
  // /settings/teams/create page is gone.
  const [createOpen, setCreateOpen] = useState(false);
  const handleCreateTeam = useCallback(() => {
    setCreateOpen(true);
  }, []);
  const handleTeamCreated = useCallback(
    (teamId: string) => {
      push(`/settings/teams/${teamId}`);
    },
    [push],
  );

  const columns = useTeamsColumns({
    variant: "settings",
    onNavigateToTeam: handleNavigateToTeam,
    onViewTeamMembers: handleViewTeamMembers,
  });
  const tableView = useTableViewSettings("teams:settings", {
    defaultHiddenColumnIds: TEAMS_SETTINGS_DEFAULT_HIDDEN_COLUMN_IDS,
  });

  return (
    <Box direction="column" fill="flex">
      <PageHeader
        title="Teams"
        actions={
          canCreateTeam ? (
            <Button
              size={isMobile ? "icon-xs" : "xs"}
              onClick={handleCreateTeam}
            >
              {/* Raw string: a bare Text element pins 14px into the 24px
                * rung; a string child inherits the button's own 12px font. */}
              {isMobile ? <Icon name="plus" /> : "Create Team"}
            </Button>
          ) : undefined
        }
      />
      <Separator />
      {/* The shared table band: narrowing tools (filter, search) sit in the
          toolbar under the header, not in the header's action cluster. */}
      {!isTrulyEmpty && (
        <DataTableToolbar>
          <DataTableToolbarActions>
            <DataTableFilterButton
              filters={teamFilters.filters}
              activeFilterCount={teamFilters.activeFilterCount}
              clearAll={teamFilters.clearAll}
              setFilterValues={teamFilters.setFilterValues}
              renderEntityFilter={renderTableEntityFilter}
              tooltip="Quickly filter teams"
            />
            <ViewSearch
              onValueChange={setSearchValue}
              size="xs"
            />
          </DataTableToolbarActions>
        </DataTableToolbar>
      )}
      <TeamsTable
        variant="settings"
        gutterTop="md"
        where={where}
        orderBy={orderBy}
        sorting={sorting}
        onSortingChange={setSorting}
        searchValue={searchValue}
        filtered={teamFilters.activeFilterCount > 0}
        columns={columns}
        tableViewProps={tableView.tableProps}
        // Actions-column circle-plus: the built-in Show columns / Available
        // picker.
        onColumnVisibilityChange={tableView.settingsProps.onColumnVisibilityChange}
        onNavigateToTeam={handleNavigateToTeam}
        onDeleteTeam={handleDeleteTeam}
        // Same gate as the header button — one permission, both affordances.
        onCreateTeam={canCreateTeam ? handleCreateTeam : undefined}
        bulkActions={bulkActions}
        onTotalChange={setTeamTotal}
      />

      <CreateTeamDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={handleTeamCreated}
      />

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete "{deleteTarget?.teamName}" team?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Projects assigned to this Team are unassigned. This cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" />}>
              <Text>Cancel</Text>
            </AlertDialogClose>
            <Button onClick={confirmDelete}>
              <Text>Delete Team</Text>
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </Box>
  );
}

export const Route = createFileRoute("/(app)/$workspace/settings/teams/")({
  component: withPermission("manage.teams", SettingsTeamsPage),
});
